<template>
  <section class="container py-10 prose">
    <h2>Luxury Chauffeur Services with Enhanced Security</h2>
    <p>Driveczech offers an exclusive combination of luxury chauffeur services and comprehensive security solutions for
      high-profile clients such as celebrities, politicians, and the ultra-wealthy. Our team provides tailored services
      to ensure your safety and privacy, delivering a seamless, discreet, and luxurious travel experience across Prague
      and the Czech Republic.</p>

    <p>Your journey with Driveczech is more than just a ride—it's a secure, premium experience. Whether you're visiting
      for business, leisure, or attending high-profile events, our dedicated team of professional drivers and security
      personnel ensures that every detail is handled with precision and care.</p>

    <img src="/images/security-driver-passanger.webp" alt="Luxury Chauffeur and Security Services">

    <h3>Key Features of Our VIP Transportation and Security Services:</h3>

    <ul>
      <li>Luxury chauffeur services with high-end vehicles like the Mercedes-Maybach, Mercedes-Benz S-Class, and more
        for ultimate comfort and discretion.</li>
      <li>Armed or unarmed professional security personnel with extensive experience in the armed forces, ensuring
        top-level protection during your stay.</li>
      <li>Close cooperation with your personal security team to guarantee all potential risks are covered during your
        visit, including high-profile public appearances, private events, or personal leisure time.</li>
      <li>Bullet-proof vests available for additional protection during travel or high-risk situations.</li>
      <li>Discreet security services for private events, business meetings, and public appearances, ensuring your
        anonymity and safety at all times.</li>
      <li>Personalized Meet & Greet airport service, where our chauffeurs meet you directly at the VIP terminal, helping
        with luggage and providing a warm welcome.</li>
      <li>24/7 security presence, including guarding hotel entrances to prevent unauthorized access to your room and
        ensure your privacy.</li>
      <li>Support for high-risk environments, such as nightclubs or social events, with trained security staff prepared
        to handle any situation.</li>
      <li>Specialized communication equipment to ensure secure and private discussions during your travel or at
        meetings.</li>
    </ul>

    <!-- <img src="example.webp" alt="VIP Security with Chauffeur Services"> -->

    <h2>Personalized Security for Celebrities, Politicians, and VIPs</h2>

    <p>At Driveczech, we understand that high-profile clients require exceptional levels of security and privacy. That’s
      why we offer personalized security plans that are tailored to your specific needs. Whether you're a celebrity,
      politician, or international VIP, our services ensure you remain safe and protected at all times.</p>

    <h3>What Makes Our Security Services Unique?</h3>

    <ul>
      <li>We offer armed or unarmed protection, allowing you to choose the level of security that best suits your needs
        and preferences.</li>
      <li>Our bodyguards are professionals with years of experience in the armed forces, ensuring that they are equipped
        to handle any risk or threat.</li>
      <li>We work seamlessly with your own personal security team to coordinate protection strategies for events,
        transportation, and private moments.</li>
      <li>Our security personnel are trained to remain discreet, ensuring that your safety is never compromised, but
        neither is your privacy or comfort.</li>
      <li>For extra protection, we provide bullet-proof vests for our clients and security staff, offering peace of mind
        during high-risk situations.</li>
    </ul>

    <!-- <img src="example.webp" alt="VIP Chauffeur and Security Protection"> -->

    <h2>Close Protection and Travel Security</h2>

    <p>We understand that your time is valuable. Driveczech’s security services are designed to provide you with peace
      of mind so that you can focus on your priorities while we handle your safety. Whether you are traveling across
      Prague or attending exclusive events, our team ensures that your movements are secure, seamless, and comfortable.
    </p>

    <p>From providing round-the-clock protection to securing hotel rooms and travel routes, Driveczech offers
      comprehensive solutions for every moment of your stay in Czechia. No matter the level of security required, we are
      here to ensure that you are always protected.</p>

    <!-- <img src="example.webp" alt="Luxury and Secure Chauffeur Services"> -->

    <h2>Contact Us for Custom Security and Transportation Services</h2>

    <p>Your needs are unique, and so are our services. At Driveczech, we believe in offering personalized security and
      transportation plans to suit your specific requirements. For detailed inquiries or to discuss your upcoming visit
      to Prague, please contact us at <a href="mailto:info@driveczech.com">info@driveczech.com</a>.</p>

    <p>We will respond within 12 hours to provide a customized solution tailored to your security and luxury
      transportation needs. Rest assured, from the moment you arrive in Prague, Driveczech will take care of every
      detail to ensure your experience is safe, luxurious, and stress-free.</p>

    <!-- <img src="example.webp" alt="Secure Chauffeur Service in Prague"> -->
  </section>

  <section class="bg-indigo-100">
    <div class="container py-10 prose">
      <h2>Case Study 1: VIP Arrival and Secure Escort to the City Center</h2>

      <p>It’s a calm afternoon at Terminal 3 of Prague’s private jet terminal. The sleek private jet of our high-profile
        client, a globally renowned celebrity, touches down smoothly on the runway. The moment the jet comes to a halt,
        two of Driveczech’s luxury Mercedes-Maybachs pull up alongside the plane on the tarmac. Both vehicles are
        pristine, ready to whisk the client away without delay.</p>

      <p>As the jet’s door opens, the client is met by a personal security guard dressed discreetly in a professional
        suit. With the utmost precision and discretion, the client is escorted down the steps and into the comfort of the
        Maybach. While the main luxury vehicle pulls away, another seemingly average car, blending into the regular flow
        of traffic, follows closely behind. This vehicle is filled with a secondary security team trained to monitor the
        surroundings, constantly scanning for any potential threats along the route.</p>

      <p>As the convoy approaches the city center, our team uses advanced communication tools to check every intersection,
        traffic signal, and bypass to ensure the route is clear. If there were to be any potential danger—a stalled
        vehicle or suspicious activity—the secondary team would discreetly intervene, keeping the client unaware of any
        issues. The client arrives at their luxurious hotel safely and swiftly, having enjoyed an entirely seamless
        journey, completely unaware of the meticulous precautions taken to ensure their safety.</p>

      <!-- <img src="example.webp" alt="VIP Airport Pickup and Secure Escort"> -->
    </div>
  </section>

  <section class="container py-10 prose">
    <h2>Case Study 2: Managing Crowds with Close Protection and Strategic Deception</h2>

    <p>A high-profile political figure, known for drawing large crowds, arrives at Prague’s premier five-star hotel.
      Word of their arrival has already spread, and the hotel’s entrance is crowded with eager fans and curious
      onlookers. The situation could quickly become overwhelming, but Driveczech’s security team is already in position,
      having anticipated the situation.</p>

    <p>To ensure the safety and comfort of the client, two plans have been prepared. The first involves the use of a
      decoy car—a standard luxury vehicle pulls up in front of the hotel, drawing the attention of the waiting crowd.
      While this decoy vehicle captures their focus, the real vehicle, escorted by security, quietly slips around to the
      hotel’s back entrance, avoiding the commotion entirely. The client is safely inside the hotel without a single fan
      even realizing they had arrived.</p>

    <p>In another scenario, the client chooses to face the crowd. The team takes a different approach: the guards form a
      protective circle, ensuring fans remain at a safe distance. With precision and coordination, they guide the client
      directly through the front entrance, creating a secure passage while maintaining the client’s dignity and
      reputation. The presence of security is subtle, ensuring the experience feels smooth and elegant rather than
      intrusive.</p>
  </section>

  <ContactForm
    title="Contact us"
    mail-subject="Elite security services - Driveczech"
    v-slot="{ model }"
  >
    <UFormGroup label="Select service">
      <USelectMenu v-model="model['Services']" :options="[
        'Armed Security',
        'Unarmed Security',
        'Meet & Greet VIP Airport Service with security guards',
        '24/7 Security Presence',
        'Custom Security & Transportation Plan'
      ]" multiple />
    </UFormGroup>

    <DateTimeInputs v-model="model['Date and time']" />

    <UFormGroup label="Name">
      <UInput v-model="model['Name']" placeholder="Full Name" autocomplete="name" />
    </UFormGroup>

    <UFormGroup label="Phone">
      <UInput v-model="model['Phone']" placeholder="+19876543210" autocomplete="tel" />
    </UFormGroup>

    <UFormGroup label="E-mail" required>
      <UInput v-model="model.email" placeholder="your@email.com" type="email" autocomplete="email" />
    </UFormGroup>

    <UFormGroup label="Message">
      <UTextarea v-model="model['Message']" placeholder="Your requirements, flight number, child seats, etc." />
    </UFormGroup>
  </ContactForm>
</template>
